import { useEffect } from "react";
import { createPortal } from "react-dom";

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { XMarkIcon } from "@heroicons/react/24/outline";

import {
  IProductionDashboardAssembly,
  IProductionDashboardBay,
  IProductionDashboardKpis,
} from "../../models/ProductionDashboard";
import classNames from "../../utils/classNames";
import TvBayTile from "./TvBayTile";

interface TvModeOverlayProps {
  bays: IProductionDashboardBay[];
  assemblies: IProductionDashboardAssembly[];
  kpis?: IProductionDashboardKpis;
  now: DateTime;
  busyVehicleIds?: Set<string>;
  completedTodayCount: number;
  triageCount: number;
  depollutionCount: number;
  hideNonAssembly?: boolean;
  onClose: () => void;
}

const vehicleName = (v: { combinedMake: string | null; combinedModel: string | null }) =>
  [v.combinedMake, v.combinedModel].filter(Boolean).join(" ");

// The Bays view blown up for a screen on the wall. Covers the whole window (and asks the browser
// for fullscreen, which it may refuse - the overlay works either way); Esc, leaving fullscreen or
// the corner button all close it.
export default function TvModeOverlay({
  bays,
  assemblies,
  kpis,
  now,
  busyVehicleIds,
  completedTodayCount,
  triageCount,
  depollutionCount,
  hideNonAssembly = false,
  onClose,
}: TvModeOverlayProps) {
  const { t } = useTranslation("production");

  useEffect(() => {
    const root = document.documentElement;
    let requested = false;

    if (root.requestFullscreen && !document.fullscreenElement) {
      requested = true;
      root.requestFullscreen().catch(() => {
        requested = false;
      });
    }

    const handleFullscreenChange = () => {
      // Only a *departure* from fullscreen closes the overlay - the entry fires this event too.
      if (requested && !document.fullscreenElement) onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("keydown", handleKeyDown);
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => undefined);
      }
    };
  }, [onClose]);

  const dueNext = bays
    .map((bay) => ({ bay, vehicle: bay.dueNext?.[0] }))
    .filter((entry) => !!entry.vehicle);

  // Portalled to <body>: DashboardLayout's <main> is its own stacking context (z-index 0), so an
  // overlay rendered inside it can never rise above the sidebar, topbar or chat bubble.
  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex flex-col gap-5 bg-gray-950 p-8 text-gray-50 overflow-hidden"
      role="dialog"
      aria-modal="true"
      aria-label={t("TV mode")}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-baseline gap-5">
          <span className="text-6xl font-semibold leading-none tabular-nums">
            {now.toFormat("HH:mm")}
          </span>
          <span className="text-xl text-gray-400">{now.toFormat("cccc d LLLL")}</span>
        </div>
        <div className="flex items-center gap-3">
          {kpis && (
            <div className="flex items-baseline gap-2 rounded-xl border border-gray-800 bg-gray-900 px-5 py-3">
              <span className="text-4xl font-semibold leading-none tabular-nums">
                {kpis.baysInUse.used}/{kpis.baysInUse.total}
              </span>
              <span className="text-base text-gray-400">{t("bays in use")}</span>
            </div>
          )}
          <div className="flex items-baseline gap-2 rounded-xl border border-gray-800 bg-gray-900 px-5 py-3">
            <span className="text-4xl font-semibold leading-none tabular-nums">
              {completedTodayCount}
            </span>
            <span className="text-base text-gray-400">{t("dismantled today")}</span>
          </div>
          {kpis && (
            <div className="flex items-baseline gap-2 rounded-xl border border-gray-800 bg-gray-900 px-5 py-3">
              <span className="text-4xl font-semibold leading-none tabular-nums">
                {kpis.partsRemoved.total}
              </span>
              <span className="text-base text-gray-400">{t("parts off")}</span>
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            className="ml-2 inline-flex h-12 w-12 items-center justify-center rounded-xl border border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white"
            aria-label={t("Exit TV mode")}
            title={t("Exit TV mode")}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
      </div>

      {bays.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-2xl text-gray-400">
          {t("No dismantling bays configured.")}
        </div>
      ) : (
        <div
          className={classNames(
            "flex-1 min-h-0 grid auto-rows-fr gap-5",
            bays.length <= 4
              ? "grid-cols-2"
              : bays.length <= 8
                ? "grid-cols-4"
                : "grid-cols-5"
          )}
        >
          {bays.map((bay) => (
            <TvBayTile
              key={bay.id}
              bay={bay}
              assemblies={assemblies}
              now={now}
              busyVehicleIds={busyVehicleIds}
              hideNonAssembly={hideNonAssembly}
            />
          ))}
        </div>
      )}

      <div className="flex items-center gap-5 rounded-xl border border-gray-800 bg-gray-900 px-6 h-16 text-xl overflow-hidden">
        <span className="text-sm font-bold uppercase tracking-widest text-gray-400 shrink-0">
          {t("Due on next")}
        </span>
        <div className="flex items-center gap-5 min-w-0 overflow-hidden whitespace-nowrap">
          {dueNext.length === 0 ? (
            <span className="text-gray-400">{t("Nothing planned")}</span>
          ) : (
            dueNext.map(({ bay, vehicle }, i) => (
              <span key={bay.id} className="flex items-center gap-5">
                {i > 0 && <span className="text-gray-700">|</span>}
                <span>
                  <strong>{bay.name}</strong>{" "}
                  <span className="text-gray-300">
                    {vehicle && [vehicle.vrm, vehicleName(vehicle)].filter(Boolean).join(" ")}
                  </span>
                </span>
              </span>
            ))
          )}
        </div>
        <span className="ml-auto shrink-0 text-base text-gray-400">
          {t("Triage")} {triageCount} · {t("Depollution")} {depollutionCount}
        </span>
      </div>
    </div>,
    document.body
  );
}
